// src/pages/document/DocumentWorkspacePage.tsx
// Document Workspace — unified page for wiki/zettelkasten and formal document management.
// Wiki mode: tree, grid, table, graph views + spaces sidebar + favorites + recent
// Formal mode: registry, pipeline kanban, review queue, expiring, acknowledgments + compliance dashboard

import { useEffect, useCallback, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  ErrorAlert,
  Badge,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
} from '@react/components';

import { ScopeProvider } from '@/components/ScopeProvider';
import { WorkspaceLayout, useWorkspace } from '@/layouts/WorkspaceLayout';
import { SplitView } from '@/components/SplitView';
import { DocumentTreeView } from '@/components/DocumentTreeView';
import { DocumentGridView } from '@/components/DocumentGridView';
import { DocumentRegistryView } from '@/components/DocumentRegistryView';
import { DocumentReviewQueue } from '@/components/DocumentReviewQueue';
import { DocumentExpiringView } from '@/components/DocumentExpiringView';
import { AcknowledgmentPanel } from '@/components/AcknowledgmentPanel';
import { DocumentGraphView } from '@/components/document/DocumentGraphView';
import { DocumentPipelineView } from '@/components/document/DocumentPipelineView';
import { DocumentSpacesSidebar } from '@/components/document/DocumentSpacesSidebar';
import { ComplianceDashboard } from '@/components/document/ComplianceDashboard';
import { DOCUMENT_WORKSPACE_CONFIG } from '@/features/workspace/logic';
import { useScope } from '@/hooks/useScope';

import type { DocumentTreeNode } from '@/features/document/types';
import type { WorkspaceMode } from '@/features/document/types';
import {
  buildDocumentTree,
  buildDocumentGraph,
  computeDocumentStats,
  computeComplianceStats,
  classifyDocumentsIntoSpaces,
  getDocumentBreadcrumbs,
  getEntityId,
  getDocumentStatus,
  getDocumentTitle,
  getDocumentOwner,
  getDocumentType,
  getDocumentIcon,
  getStatusMapping,
  documentStatusMap,
  documentTypeMap,
  formatRelativeTime,
  getInitials,
  getAvatarColor,
  filterDocuments,
  sortDocumentsByActivity,
  FORMAL_DOCUMENT_VIEWS,
  DEFAULT_DOCUMENT_SPACES,
  filterByReviewStatus,
  filterExpiring,
  getRecentDocuments,
  getFavoriteDocIds,
  toggleFavoriteDoc,
  filterFavorites,
  findDailyNote,
  getDailyNoteTitle,
} from '@/features/document/logic';

// ---------------------------------------------------------------------------
// Stats bar
// ---------------------------------------------------------------------------

function DocumentStatsBar({ docs, t }: { docs: any[]; t: (k: string) => string }) {
  const stats = useMemo(() => computeDocumentStats(docs), [docs]);

  const items: { labelKey: string; count: number; variant: string }[] = [
    { labelKey: 'document.stats.total', count: stats.total, variant: 'default' },
    { labelKey: 'document.stats.draft', count: stats.draft, variant: 'secondary' },
    { labelKey: 'document.stats.published', count: stats.published, variant: 'success' },
    { labelKey: 'document.stats.inReview', count: stats.inReview, variant: 'warning' },
    { labelKey: 'document.stats.archived', count: stats.archived, variant: 'secondary' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-3">
      {items.map(item => (
        <div key={item.labelKey} className="flex items-center gap-1.5">
          <Badge variant={item.variant as any} size="sm">{item.count}</Badge>
          <span className="text-xs text-muted-foreground">{t(item.labelKey)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumbs
// ---------------------------------------------------------------------------

function DocumentBreadcrumbs({
  docId,
  allDocs,
  onNavigate,
  t,
}: {
  docId: string;
  allDocs: any[];
  onNavigate: (id: string | undefined) => void;
  t: (k: string) => string;
}) {
  const crumbs = useMemo(
    () => getDocumentBreadcrumbs(docId, allDocs),
    [docId, allDocs],
  );

  if (crumbs.length === 0) return null;

  return (
    <nav className="flex items-center gap-1 text-sm" aria-label={t('document.breadcrumbs')}>
      <button
        onClick={() => onNavigate(undefined)}
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        {t('document.root')}
      </button>
      {crumbs.map((crumb, i) => (
        <span key={crumb.id} className="flex items-center gap-1">
          <svg className="h-3 w-3 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          {i < crumbs.length - 1 ? (
            <button
              onClick={() => onNavigate(crumb.id)}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {crumb.title || t('common.untitled')}
            </button>
          ) : (
            <span className="text-foreground font-medium">
              {crumb.title || t('common.untitled')}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Favorite toggle button
// ---------------------------------------------------------------------------

function FavoriteButton({
  docId,
  favoriteIds,
  onToggle,
}: {
  docId: string;
  favoriteIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  const isFav = favoriteIds.has(docId);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(docId); }}
      className="p-1 rounded hover:bg-muted transition-colors"
      aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
    >
      <svg
        className={`w-3.5 h-3.5 ${isFav ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`}
        viewBox="0 0 24 24"
        fill={isFav ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={2}
      >
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Table view (inline — uses @react/components Table)
// ---------------------------------------------------------------------------

function DocumentTableView({
  documents,
  selectedDocId,
  onSelect,
  onOpen,
  locale,
  t,
  favoriteIds,
  onToggleFavorite,
}: {
  documents: any[];
  selectedDocId?: string;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
  locale: string;
  t: (k: string) => string;
  favoriteIds: Set<string>;
  onToggleFavorite: (id: string) => void;
}) {
  if (documents.length === 0) {
    return (
      <Table>
        <TableBody>
          <TableEmpty colSpan={7}>{t('document.noItems')}</TableEmpty>
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-8" />
            <TableHead className="w-8" />
            <TableHead>{t('document.title')}</TableHead>
            <TableHead className="w-28">{t('document.docType')}</TableHead>
            <TableHead className="w-28">{t('document.status')}</TableHead>
            <TableHead className="w-20">{t('document.author')}</TableHead>
            <TableHead className="w-28">{t('document.updated')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((doc: any) => {
            const id = getEntityId(doc);
            const status = getDocumentStatus(doc);
            const docType = getDocumentType(doc);
            const owner = getDocumentOwner(doc);
            const statusInfo = getStatusMapping(documentStatusMap, status);
            const typeInfo = getStatusMapping(documentTypeMap, docType);
            const iconPath = getDocumentIcon(doc);

            return (
              <TableRow
                key={id}
                className={`h-11 cursor-pointer transition-colors hover:bg-muted/50 ${
                  id === selectedDocId ? 'bg-primary/5' : ''
                }`}
                onClick={() => onSelect(id)}
                onDoubleClick={() => onOpen(id)}
              >
                {/* Favorite */}
                <TableCell>
                  <FavoriteButton docId={id} favoriteIds={favoriteIds} onToggle={onToggleFavorite} />
                </TableCell>

                {/* Icon */}
                <TableCell>
                  <svg
                    className="h-4 w-4 text-muted-foreground"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
                  </svg>
                </TableCell>

                {/* Title */}
                <TableCell>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-foreground truncate block">
                      {doc.title || t('common.untitled')}
                    </span>
                    {doc.description && (
                      <span className="text-xs text-muted-foreground truncate block mt-0.5">
                        {doc.description.slice(0, 80)}
                      </span>
                    )}
                  </div>
                </TableCell>

                {/* Type */}
                <TableCell>
                  <Badge variant={typeInfo.variant as any} size="sm">
                    {t(typeInfo.labelKey)}
                  </Badge>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Badge variant={statusInfo.variant as any} size="sm">
                    {t(statusInfo.labelKey)}
                  </Badge>
                </TableCell>

                {/* Author */}
                <TableCell>
                  {owner ? (
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold text-white flex-shrink-0"
                      style={{ backgroundColor: getAvatarColor(owner) }}
                      title={owner}
                    >
                      {getInitials(owner)}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">--</span>
                  )}
                </TableCell>

                {/* Updated */}
                <TableCell>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {doc.updated_at ? formatRelativeTime(doc.updated_at, locale) : '--'}
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function DocumentWorkspaceSkeleton({ view }: { view: string }) {
  if (view === 'grid') {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border p-4 animate-pulse">
            <div className="flex items-start gap-2.5 mb-3">
              <div className="w-8 h-8 rounded-md bg-muted" />
              <div className="flex-1">
                <div className="h-4 w-3/4 bg-muted rounded mb-1" />
                <div className="h-3 w-1/2 bg-muted rounded" />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="h-5 w-16 bg-muted rounded-full" />
              <div className="h-5 w-5 bg-muted rounded-full" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (view === 'graph') {
    return (
      <div className="rounded-lg border border-border bg-background animate-pulse" style={{ minHeight: 400 }}>
        <div className="flex items-center justify-center h-[400px]">
          <div className="w-32 h-4 bg-muted rounded" />
        </div>
      </div>
    );
  }

  // Default tree/table skeleton
  return (
    <div className="space-y-1">
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2 animate-pulse">
          <div className="h-3.5 w-3.5 rounded bg-muted" />
          <div className="h-4 w-4 rounded bg-muted" />
          <div className="h-4 flex-1 rounded bg-muted" />
          <div className="h-5 w-16 rounded-full bg-muted" />
          <div className="h-3 w-16 rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mode switcher (segmented control)
// ---------------------------------------------------------------------------

function ModeSwitcher({
  mode,
  onModeChange,
  t,
}: {
  mode: WorkspaceMode;
  onModeChange: (m: WorkspaceMode) => void;
  t: (k: string) => string;
}) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5">
      <button
        onClick={() => onModeChange('wiki')}
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          mode === 'wiki'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        {t('document.mode.wiki')}
      </button>
      <button
        onClick={() => onModeChange('formal')}
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          mode === 'formal'
            ? 'bg-background text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        {t('document.mode.formal')}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wiki view switcher (tree/grid/table/graph)
// ---------------------------------------------------------------------------

function WikiViewSwitcher({
  activeView,
  onViewChange,
  t,
}: {
  activeView: string;
  onViewChange: (v: string) => void;
  t: (k: string) => string;
}) {
  const views = [
    { id: 'tree', labelKey: 'document.view.tree', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z' },
    { id: 'grid', labelKey: 'document.view.grid', icon: 'M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z' },
    { id: 'table', labelKey: 'document.view.table', icon: 'M3 10h18M3 14h18M3 18h18M3 6h18' },
    { id: 'graph', labelKey: 'document.view.graph', icon: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 18a6 6 0 100-12 6 6 0 000 12zM12 14a2 2 0 100-4 2 2 0 000 4z' },
  ];

  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-muted/40 p-0.5">
      {views.map((v) => (
        <button
          key={v.id}
          onClick={() => onViewChange(v.id)}
          className={`p-1.5 rounded-md transition-colors ${
            activeView === v.id
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          title={t(v.labelKey)}
          aria-label={t(v.labelKey)}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d={v.icon} />
          </svg>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formal sub-tab bar
// ---------------------------------------------------------------------------

function FormalViewTabs({
  activeView,
  onViewChange,
  t,
}: {
  activeView: string;
  onViewChange: (id: string) => void;
  t: (k: string) => string;
}) {
  return (
    <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
      {FORMAL_DOCUMENT_VIEWS.map((view) => (
        <button
          key={view.id}
          onClick={() => onViewChange(view.id)}
          className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
            activeView === view.id
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
          }`}
        >
          {t(view.labelKey)}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formal mode content
// ---------------------------------------------------------------------------

function FormalModeContent({
  documents,
  activeView,
  onSelect,
  onOpen,
}: {
  documents: any[];
  activeView: string;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
}) {
  const reviewDocs = useMemo(() => filterByReviewStatus(documents), [documents]);
  const expiringDocs = useMemo(() => filterExpiring(documents, 30), [documents]);

  const handleApprove = useCallback(async (_id: string) => {
    // TODO: integrate with ApprovalService
  }, []);

  const handleReject = useCallback(async (_id: string) => {
    // TODO: integrate with ApprovalService
  }, []);

  const handleRequestChanges = useCallback(async (_id: string) => {
    // TODO: integrate with ApprovalService
  }, []);

  const handleRenew = useCallback(async (_id: string) => {
    // TODO: integrate with DocumentService.CreateRevision
  }, []);

  const handleSendReminder = useCallback(async (_userId: string) => {
    // TODO: integrate with AcknowledgmentService
  }, []);

  const handleRequestAcknowledgment = useCallback(async () => {
    // TODO: integrate with AcknowledgmentService
  }, []);

  switch (activeView) {
    case 'registry':
      return (
        <DocumentRegistryView
          documents={documents}
          onSelect={onSelect}
        />
      );
    case 'pipeline':
      return (
        <DocumentPipelineView
          documents={documents}
          onSelect={onSelect}
          onOpen={onOpen}
        />
      );
    case 'review_queue':
      return (
        <DocumentReviewQueue
          documents={reviewDocs}
          onApprove={handleApprove}
          onReject={handleReject}
          onRequestChanges={handleRequestChanges}
        />
      );
    case 'expiring':
      return (
        <DocumentExpiringView
          documents={expiringDocs}
          onRenew={handleRenew}
          onSelect={onSelect}
        />
      );
    case 'acknowledgments':
      return (
        <AcknowledgmentPanel
          documentId=""
          acknowledgments={[]}
          total={0}
          onSendReminder={handleSendReminder}
          onRequestAcknowledgment={handleRequestAcknowledgment}
        />
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Inner workspace (requires scope + workspace context)
// ---------------------------------------------------------------------------

const DocumentWorkspaceInner = observer(() => {
  const store = useDocument();
  const { t, locale } = useI18n();
  const scope = useScope();
  const { state } = useWorkspace();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const mode = (searchParams.get('mode') === 'formal' ? 'formal' : 'wiki') as WorkspaceMode;
  const [formalView, setFormalView] = useState(FORMAL_DOCUMENT_VIEWS[0].id);
  const [wikiView, setWikiView] = useState<string>('tree');

  const handleModeChange = useCallback((m: WorkspaceMode) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('mode', m);
      return next;
    });
  }, [setSearchParams]);

  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>(undefined);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => getFavoriteDocIds());
  const [activeSpaceId, setActiveSpaceId] = useState('all');
  const [showSidebar, setShowSidebar] = useState(true);

  // Load documents — send status filter to comply with required filter policy
  useEffect(() => {
    const filters: Record<string, any> = {};
    const statusFilterEntry = state.filters?.find((f: any) => f.field === 'status');
    if (statusFilterEntry) {
      const statusMap: Record<string, number> = {
        draft: 1, in_review: 2, pending_approval: 3, approved: 4,
        published: 5, revision: 6, obsolete: 7, archived: 8,
      };
      const val = Array.isArray(statusFilterEntry.value) ? statusFilterEntry.value[0] : statusFilterEntry.value;
      const statusVal = statusMap[val as string];
      if (statusVal) filters.status_filter = statusVal;
    }
    if (!filters.status_filter && !state.search) {
      filters.status_filter = 1;
    }
    if (state.search) {
      filters.search_query = state.search;
    }

    const params = {
      pagination: { cursor: '', limit: 500, direction: 'forward' as const },
      sort: [],
      date_range: {} as any,
      filters: filters as any,
    };
    store.ListDocuments(params);
  }, [scope.productId, state.filters, state.search]);

  const documents: any[] = store.items;

  // Filter and sort
  const processedDocs = useMemo(() => {
    const filtered = filterDocuments(documents, state.search, 'all');
    return sortDocumentsByActivity(filtered);
  }, [documents, state.search]);

  // Build tree
  const tree = useMemo<DocumentTreeNode[]>(
    () => buildDocumentTree(processedDocs),
    [processedDocs],
  );

  // Build graph (for graph view)
  const graph = useMemo(
    () => wikiView === 'graph'
      ? buildDocumentGraph(processedDocs, new Map(), selectedDocId ?? undefined)
      : { nodes: [], edges: [] },
    [processedDocs, wikiView, selectedDocId],
  );

  // Spaces
  const spaces = useMemo(
    () => classifyDocumentsIntoSpaces(processedDocs, DEFAULT_DOCUMENT_SPACES),
    [processedDocs],
  );

  // Compliance stats (for formal mode)
  const complianceStats = useMemo(
    () => mode === 'formal' ? computeComplianceStats(processedDocs) : null,
    [processedDocs, mode],
  );

  // Recent docs
  const recentDocs = useMemo(() => getRecentDocuments(), []);

  // Favorite docs (for sidebar)
  const favoriteDocs = useMemo(() => {
    return filterFavorites(processedDocs, favoriteIds).map((d: any) => ({
      id: getEntityId(d),
      title: getDocumentTitle(d),
      type: getDocumentType(d),
    }));
  }, [processedDocs, favoriteIds]);

  const handleSelect = useCallback((id: string) => setSelectedDocId(id), []);
  const handleOpen = useCallback(
    (id: string) => navigate(`/documents/${id}`),
    [navigate],
  );
  const handleCloseSplit = useCallback(() => setSelectedDocId(null), []);

  const handleBreadcrumbNavigate = useCallback((id: string | undefined) => {
    setCurrentFolderId(id);
  }, []);

  const handleToggleFavorite = useCallback((id: string) => {
    setFavoriteIds(toggleFavoriteDoc(id));
  }, []);

  const handleNavigateToDoc = useCallback((id: string) => {
    navigate(`/documents/${id}`);
  }, [navigate]);

  const handleCreateDailyNote = useCallback(() => {
    const existing = findDailyNote(processedDocs, new Date());
    if (existing) {
      navigate(`/documents/${getEntityId(existing)}`);
    } else {
      navigate(`/documents/new?title=${encodeURIComponent(getDailyNoteTitle(new Date()))}`);
    }
  }, [processedDocs, navigate]);

  // Split detail placeholder
  const splitDetail = selectedDocId ? (
    <div className="p-4">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">{t('document.details')}</h3>
          <FavoriteButton docId={selectedDocId} favoriteIds={favoriteIds} onToggle={handleToggleFavorite} />
        </div>
        <p className="text-sm text-muted-foreground">
          {t('document.detailPlaceholder')}
        </p>
        <Button size="sm" onClick={() => handleOpen(selectedDocId)}>
          {t('document.openFull')}
        </Button>
      </div>
    </div>
  ) : null;

  // Error state
  if (store.error && !store.isLoading) {
    return (
      <ErrorAlert
        error={store.error}
        dismissible
        onDismiss={() => store.clearError()}
      />
    );
  }

  // Loading state
  if (store.isLoading && documents.length === 0) {
    return <DocumentWorkspaceSkeleton view={wikiView} />;
  }

  // Empty state
  if (!store.isLoading && documents.length === 0) {
    return (
      <div className="py-16 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <p className="text-lg font-medium text-foreground">{t('document.noItems')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t('document.noItemsDescription')}</p>
        <div className="flex items-center justify-center gap-3 mt-4">
          <Button onClick={() => navigate('/documents/new')}>
            {t('document.create')}
          </Button>
          <Button variant="secondary" onClick={handleCreateDailyNote}>
            {t('document.dailyNote')}
          </Button>
        </div>
      </div>
    );
  }

  // Render wiki view content
  const renderWikiView = () => {
    switch (wikiView) {
      case 'grid':
        return (
          <DocumentGridView
            documents={processedDocs}
            selectedDocId={selectedDocId ?? undefined}
            onSelect={handleSelect}
            onOpen={handleOpen}
          />
        );
      case 'table':
        return (
          <DocumentTableView
            documents={processedDocs}
            selectedDocId={selectedDocId ?? undefined}
            onSelect={handleSelect}
            onOpen={handleOpen}
            locale={locale}
            t={t}
            favoriteIds={favoriteIds}
            onToggleFavorite={handleToggleFavorite}
          />
        );
      case 'graph':
        return (
          <DocumentGraphView
            graph={graph}
            onSelectNode={handleSelect}
            onOpenNode={handleOpen}
            selectedNodeId={selectedDocId ?? undefined}
          />
        );
      case 'tree':
      default:
        return (
          <DocumentTreeView
            tree={tree}
            selectedDocId={selectedDocId ?? undefined}
            onSelect={handleSelect}
            onOpen={handleOpen}
          />
        );
    }
  };

  return (
    <div className="flex gap-0 -mx-4 -mt-2">
      {/* Spaces sidebar (wiki mode, desktop only) */}
      {mode === 'wiki' && showSidebar && (
        <div className="hidden xl:block">
          <DocumentSpacesSidebar
            spaces={spaces}
            activeSpaceId={activeSpaceId}
            onSelectSpace={setActiveSpaceId}
            recentDocs={recentDocs}
            favoriteDocs={favoriteDocs}
            onNavigateToDoc={handleNavigateToDoc}
            onCreateDaily={handleCreateDailyNote}
          />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0 px-4 pt-2">
        <div className="space-y-3">
          {/* Top bar: mode switcher + view switcher + stats */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <ModeSwitcher mode={mode} onModeChange={handleModeChange} t={t} />
              {mode === 'wiki' && (
                <WikiViewSwitcher activeView={wikiView} onViewChange={setWikiView} t={t} />
              )}
              {/* Sidebar toggle (desktop) */}
              {mode === 'wiki' && (
                <button
                  onClick={() => setShowSidebar(!showSidebar)}
                  className="hidden xl:inline-flex p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  aria-label={t('document.toggleSidebar')}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={showSidebar ? 'M11 19l-7-7 7-7m8 14l-7-7 7-7' : 'M13 5l7 7-7 7M5 5l7 7-7 7'} />
                  </svg>
                </button>
              )}
            </div>
            {mode === 'wiki' && <DocumentStatsBar docs={processedDocs} t={t} />}
          </div>

          {/* Formal mode: compliance dashboard + tabs */}
          {mode === 'formal' && (
            <>
              {complianceStats && <ComplianceDashboard stats={complianceStats} />}
              <FormalViewTabs activeView={formalView} onViewChange={setFormalView} t={t} />
              <FormalModeContent
                documents={processedDocs}
                activeView={formalView}
                onSelect={handleSelect}
                onOpen={handleOpen}
              />
            </>
          )}

          {/* Wiki mode content */}
          {mode === 'wiki' && (
            <>
              {/* Breadcrumbs (when inside a folder) */}
              {currentFolderId && (
                <DocumentBreadcrumbs
                  docId={currentFolderId}
                  allDocs={documents}
                  onNavigate={handleBreadcrumbNavigate}
                  t={t}
                />
              )}

              {/* Content with split view (except graph which is full-width) */}
              {wikiView === 'graph' ? (
                renderWikiView()
              ) : (
                <SplitView detail={splitDetail} onClose={handleCloseSplit}>
                  {renderWikiView()}
                </SplitView>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Exported page component (wraps in providers)
// ---------------------------------------------------------------------------

export const DocumentWorkspacePage = observer(() => {
  const navigate = useNavigate();
  const handleCreate = useCallback(() => navigate('/documents/new'), [navigate]);

  return (
    <ScopeProvider>
      <WorkspaceLayout
        config={DOCUMENT_WORKSPACE_CONFIG}
        title="document.workspace.title"
        createLabel="document.create"
        onCreateClick={handleCreate}
      >
        <DocumentWorkspaceInner />
      </WorkspaceLayout>
    </ScopeProvider>
  );
});

export default DocumentWorkspacePage;
